/**
 * Phase 11 — handler registry for `ide_client` target ops.
 *
 * Consumed by `usePlatformOpListener` which subscribes to OpenClaw's
 * auto-emitted `session.tool` EventFrames and dispatches matching
 * tool calls to the handler registered here. Per-op handler logic
 * lives in `./platform-ops-handlers/*.ts`; this file is just the
 * registry + dispatcher.
 *
 * **Thin-slice scope (X.1.3).** Currently routes one op
 * (`ide.toast.show`). Adding ops later is a single `register(...)` call.
 *
 * Design + lifecycle:
 *   ai-docs/lexiun-platform-llm-tools-guide.md §3, §4.5
 */
import type { PlatformOp } from "@lexiun/lx-platform-ops";

/**
 * Result of a handler invocation. Mirrors the catalog's generic
 * `ToolResult` envelope (guide §3.5.1). The thin slice's IDE handlers
 * are fire-and-forget; the result is logged but not currently routed
 * back to the runtime (no bidirectional correlation yet — that's X.2).
 */
export type HandlerResult =
  | { ok: true; data?: unknown }
  | { ok: false; error: { code: string; message: string } };

export interface HandlerContext {
  /** Catalog entry for the op being invoked. Handlers can read fields like
   *  `description` if they want to surface them in UI. */
  op: PlatformOp;
  /** Tool call ID from OpenClaw's tool-use loop (correlation key). */
  toolCallId?: string;
  /** Session key the action was invoked under. */
  sessionKey?: string;
}

export type Handler = (
  params: Record<string, unknown>,
  ctx: HandlerContext,
) => Promise<HandlerResult> | HandlerResult;

interface RegistryEntry {
  handler: Handler;
}

class PlatformOpsRegistry {
  private entries = new Map<string, RegistryEntry>();

  register(opName: string, handler: Handler): void {
    if (this.entries.has(opName)) {
      // Re-registration overwrites silently — matches HMR-friendly
      // behaviour. In production this only happens once per name.
    }
    this.entries.set(opName, { handler });
  }

  has(opName: string): boolean {
    return this.entries.has(opName);
  }

  /**
   * Dispatch a tool call to its registered handler. Returns:
   *  - the handler's result if a handler is registered
   *  - `IDE_TOOL_NOT_FOUND` if no handler matches (op exists in the
   *     catalog but the SPA doesn't ship a handler for it — typically
   *     a non-`ide_client` op that leaked into the listener)
   */
  async dispatch(
    opName: string,
    params: Record<string, unknown>,
    ctx: HandlerContext,
  ): Promise<HandlerResult> {
    const entry = this.entries.get(opName);
    if (!entry) {
      return {
        ok: false,
        error: {
          code: "IDE_TOOL_NOT_FOUND",
          message: `No handler registered for "${opName}". The lexide SPA may be behind the runtime catalog — refresh recommended.`,
        },
      };
    }
    try {
      return await entry.handler(params, ctx);
    } catch (err) {
      return {
        ok: false,
        error: {
          code: "IDE_HANDLER_ERROR",
          message: (err as Error).message,
        },
      };
    }
  }

  /** Snapshot of all registered op names. Used by the capabilities-publish
   *  handshake to tell the runtime which ops the SPA supports. */
  knownOps(): readonly string[] {
    return Array.from(this.entries.keys()).sort();
  }

  /** Test-only — clears all registrations. Lets vitest specs start clean. */
  _reset(): void {
    this.entries.clear();
  }
}

/** Singleton — populated at app boot by `platform-ops-handlers/index.ts`. */
export const platformOpsRegistry = new PlatformOpsRegistry();

// DEV-only accessor for Playwright + integration-test injection.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__platformOpsRegistry", {
    value: platformOpsRegistry,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
